import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
  shouldStripPostalCodeWhitespace,
  isPostalCodeLocal,
} from '../_utils/validations';
import genericHelpers from 'customer_pages/_utils/generic-helpers';
import get from 'lodash/get';
import { builderDispatch } from '../_utils/ui';
import CartItemsChangeEvents from '../_cart/cart-items-change-events';
import ZipcodeSection from './zip-code-section';
import StockInfoSection from './stock-info-section';
import ZipCodes from '../_libs/zip-codes';
import Spinner from '../_utils/spinner';
import DeliveryCalculator from '../_utils/_delivery-options/calculator';
import { pickupStoreWithEverythingInStock } from '../_cart/alternate-stores';
import { updateStoreOnServer } from './server-actions';
import { observableEvent } from '../_utils/observable-event';
import Cookies from 'js-cookie';
import { StorePickerContext } from './store-picker-context';

const hasLocalStorage = () =>
  typeof window !== 'undefined' && 'localStorage' in window;

function StockAvailability({
  copies,
  currentProducts,
  deliveryOptions,
  storeId: storeIdProp,
  selectedDeliveryOption,
  urls: {
    product_stock_availability: productStockToLocationUrl,
    update_user_postcode: updateUserPostcode,
    geolocation_to_zipcode: geolocationToZipcode,
    update_delivery_pickup: updateDeliveryPickup,
  },
  refreshOrder,
}) {
  if (
    currentProducts.length > 1 ||
    !currentProducts[0]?.check_stock_availability
  ) {
    return null;
  }

  const selectedDelivery = (deliveryOptions || []).find(
    (option) => option.key === parseInt(selectedDeliveryOption),
  );

  if (
    !selectedDelivery ||
    !['PICKUP', 'COLLECT'].includes(selectedDelivery.type)
  ) {
    return null;
  }

  const loadStoreId = useCallback(() => {
    if (storeIdProp) {
      if (hasLocalStorage()) window.localStorage.removeItem('storeId');
      return { source: 'cart', storeId: storeIdProp };
    }

    if (
      window.App &&
      window.App?.order &&
      window.App.order?.storeId &&
      parseInt(window.App.order?.no_items) > 0
    ) {
      return { source: 'order', storeId: String(window.App.order.storeId) };
    }
    const cookieStoreId = Cookies.get('assortmentStoreId');
    if (cookieStoreId) {
      return { source: 'cookie', storeId: String(cookieStoreId) };
    }

    if (hasLocalStorage()) {
      const localStoreId = window.localStorage.getItem('storeId');
      if (localStoreId) {
        return { source: 'local', storeId: String(localStoreId) };
      }
    }
    return { source: null, storeId: null };
  }, [storeIdProp]);

  const initialZipCode = ZipCodes.load();
  const initialStoreInfo = loadStoreId();

  const [zipCode, setZipCode] = useState(initialZipCode);
  const [zipCodeValid, setZipCodeValid] = useState(
    isPostalCodeLocal(initialZipCode),
  );
  const [storeInfo, setStoreInfo] = useState(initialStoreInfo);
  const [locations, setLocations] = useState({
    pickup: [],
    selected: initialStoreInfo.storeId,
    available: [],
    errorMessage: '',
    storeSource: initialStoreInfo.source,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isChangingStore, setIsChangingStore] = useState(false);
  const [alternatePickupStore, setAlternatePickupStore] = useState(null);
  const isMounting = useRef(true);
  const isUpdatingStore = useRef(false);

  useEffect(() => {
    const newStoreInfo = loadStoreId();
    if (!newStoreInfo.storeId) return;
    setStoreInfo((prev) =>
      prev.storeId === newStoreInfo.storeId ? prev : newStoreInfo,
    );
    setLocations((prev) => ({
      ...prev,
      selected: newStoreInfo.storeId,
      storeSource: newStoreInfo.source,
    }));
  }, [loadStoreId]);

  const saveStoreId = useCallback(
    (storeId) => {
      if (!storeId) {
        if (hasLocalStorage()) {
          window.localStorage.removeItem('storeId');
        }
        return;
      }

      const storeIdString = String(storeId);

      if (storeInfo.source === 'cart') {
        builderDispatch.dispatch({
          type: CartItemsChangeEvents.PICKUP_LOCATION_CHANGE,
          value: storeIdString,
        });
      } else {
        if (hasLocalStorage()) {
          window.localStorage.setItem('storeId', storeIdString);
        }
      }

      setStoreInfo((prev) => ({
        ...prev,
        storeId: storeIdString,
      }));
      setLocations((prev) => ({
        ...prev,
        selected: storeIdString,
      }));
    },
    [builderDispatch, storeInfo.source],
  );

  const handleZipCodeChangeContext = useCallback(
    (newZipCode, preferredStoreId = null) => {
      if (!isPostalCodeLocal(newZipCode)) return;
      setZipCode(newZipCode);
      setZipCodeValid(true);

      ZipCodes.save(newZipCode);
      ZipCodes.persist(updateUserPostcode, newZipCode);
      findCollectionLocations(newZipCode, false, preferredStoreId);
    },
    [updateUserPostcode],
  );

  const handleZipCodeChange = useCallback((value) => {
    const processedValue = shouldStripPostalCodeWhitespace()
      ? value.replace(/ /g, '')
      : value;

    setZipCode(processedValue);
    const isValid = isPostalCodeLocal(processedValue);
    setZipCodeValid(isValid);

    if (isValid) {
      builderDispatch.dispatch({
        type: CartItemsChangeEvents.ZIP_CODE_CHANGE,
        value: processedValue,
      });
    }
  }, []);

  const getServiceFilter = useCallback(() => {
    switch (selectedDelivery.time_hours) {
      case DeliveryCalculator.ONE_HOUR:
        return 'PHOTO_ONE_HOUR_PICKUP';
      case DeliveryCalculator.TWENTY_FOUR_HOURS:
        return 'PHOTO_SAME_DAY_PICKUP';
      default:
        return null;
    }
  }, [selectedDelivery]);

  const getAvailableStores = useCallback((data, productId, serviceFilter) => {
    return data.filter((store) => {
      const timestamp = store.orderTimestamps?.[serviceFilter]?.[productId];
      return !timestamp || timestamp.service_available === true;
    });
  }, []);

  const fetchGroupedLocations = useCallback(
    async (code) => {
      if (!productStockToLocationUrl) {
        return [];
      }
      return await genericHelpers.findLocationsForGroupedPages(
        productStockToLocationUrl,
        code,
        currentProducts[0].id,
        selectedDeliveryOption,
        copies,
      );
    },
    [
      productStockToLocationUrl,
      currentProducts,
      selectedDeliveryOption,
      copies,
    ],
  );

  const findCollectionLocations = useCallback(
    async (code, forceChangeStoreId = false, preferredStoreId = null) => {
      setLocations((prev) => ({
        ...prev,
        pickup: [],
        available: [],
        errorMessage: '',
      }));
      setIsLoading(true);

      try {
        const newLocations = await fetchGroupedLocations(code);

        const alternateStore = pickupStoreWithEverythingInStock(newLocations);

        const prismItem = {
          ...currentProducts[0],
          delivery: parseInt(selectedDeliveryOption),
          quantity: copies,
          id: 888888,
          product_title: currentProducts[0].title,
        };

        const serviceFilter = getServiceFilter();
        const availableStores = getAvailableStores(
          newLocations,
          prismItem.id,
          serviceFilter,
        );

        let newSelectedLocationId;
        const currentSelectedId =
          preferredStoreId !== null
            ? String(preferredStoreId)
            : storeInfo.storeId;

        if (forceChangeStoreId) {
          newSelectedLocationId = get(newLocations[0], 'id');
        } else if (
          currentSelectedId &&
          newLocations.some(
            (location) => String(location.id) === String(currentSelectedId),
          )
        ) {
          newSelectedLocationId = currentSelectedId;
        } else {
          newSelectedLocationId = get(newLocations[0], 'id');
        }

        setLocations((prev) => ({
          ...prev,
          pickup: newLocations,
          available: availableStores,
          selected: String(newSelectedLocationId),
        }));

        setAlternatePickupStore(alternateStore);

        if (String(newSelectedLocationId) !== String(currentSelectedId)) {
          setStoreInfo((prev) => ({
            ...prev,
            storeId: String(newSelectedLocationId),
          }));
        }

        setIsLoading(false);
      } catch (error) {
        setLocations((prev) => ({
          ...prev,
          pickup: [],
          errorMessage: String(error),
        }));
        setIsLoading(false);
      }
    },
    [
      fetchGroupedLocations,
      getServiceFilter,
      getAvailableStores,
      currentProducts,
      selectedDeliveryOption,
      copies,
      storeInfo.storeId,
    ],
  );

  const handleSelectedLocationChange = useCallback(
    (locationId) => {
      if (isUpdatingStore.current) {
        return;
      }
      isUpdatingStore.current = true;
      setIsChangingStore(true);

      updateStoreOnServer({
        url: updateDeliveryPickup,
        option: locationId,
      })
        .then(async (response) => {
          if (response?.ok) {
            await refreshOrder();
          }
        })
        .catch((error) => {
          observableEvent.record('stock-availability-store-update-fail', {
            message: String(error),
          });
        })
        .finally(() => {
          if (isMounting.current) {
            isUpdatingStore.current = false;
            setIsChangingStore(false);
          }
        });
    },
    [updateDeliveryPickup, refreshOrder, isMounting],
  );

  useEffect(() => {
    findCollectionLocations(zipCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = builderDispatch.register((payload) => {
      switch (payload.type) {
        case CartItemsChangeEvents.ZIP_CODE_CHANGE:
          handleZipCodeChangeContext(payload.value);
          break;
        case CartItemsChangeEvents.PICKUP_LOCATION_CHANGE:
          handleSelectedLocationChange(payload.value);
          break;
        default:
          break;
      }
    });
    return () => builderDispatch.unregister(token);
  }, [handleZipCodeChangeContext, handleSelectedLocationChange]);

  const handleOnSave = useCallback(
    (id, newZipCode = null) => {
      saveStoreId(id);
      if (newZipCode && isPostalCodeLocal(newZipCode)) {
        handleZipCodeChangeContext(newZipCode, id);
      } else {
        findCollectionLocations(zipCode, false, id);
      }
    },
    [saveStoreId, handleZipCodeChangeContext, findCollectionLocations, zipCode],
  );

  const storePickerContextValue = useMemo(
    () => ({
      zipCode,
      zipCodeValid,
      locations,
      isLoading,
      alternatePickupStore,
      geolocationToZipcode,
      productStockToLocationUrl,
      selectedDeliveryOption,
      copies,
      deliveryType: getServiceFilter(),
      prismId: currentProducts[0].id,
      handleOnSave,
    }),
    [
      zipCode,
      zipCodeValid,
      locations,
      isLoading,
      alternatePickupStore,
      geolocationToZipcode,
      productStockToLocationUrl,
      selectedDeliveryOption,
      copies,
      getServiceFilter,
      currentProducts,
      handleOnSave,
    ],
  );

  return (
    <div className="stock-availability">
      <ZipcodeSection
        updateUserPostcode={updateUserPostcode}
        zipCode={zipCode}
        zipCodeValid={zipCodeValid}
        onZipCodeChange={handleZipCodeChange}
        saveZipCodeChange={handleZipCodeChangeContext}
        findCollectionLocations={findCollectionLocations}
      />
      <StorePickerContext.Provider value={storePickerContextValue}>
        {isLoading || isChangingStore ? (
          <Spinner mini className="stock-availability-spinner" />
        ) : (
          <StockInfoSection />
        )}
      </StorePickerContext.Provider>
    </div>
  );
}

StockAvailability.propTypes = {
  copies: PropTypes.number,
  currentProducts: PropTypes.array,
  deliveryOptions: PropTypes.array,
  storeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedDeliveryOption: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  urls: PropTypes.object,
  refreshOrder: PropTypes.func,
};

export default StockAvailability;
